/**
 * Analytics Library
 * 
 * PostHog initialization and utilities that respect cookie consent,
 * Global Privacy Control (GPC), and Do Not Track (DNT).
 */

import posthog from 'posthog-js';
import { CONFIG } from '@/config-global';
import { readCookieConsent } from '@/hooks/use-cookie-consent';
import { devError, devLog, devWarn } from '@/utils/dev-console';

// ----------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------

let posthogInitialized = false;

// ----------------------------------------------------------------------
// Environment Check
// ----------------------------------------------------------------------

/**
 * Check if analytics should be enabled based on environment
 * Only track in production unless explicitly enabled via env var
 */
function shouldTrackAnalytics(): boolean {
  if (import.meta.env.VITE_ENABLE_ANALYTICS === 'true') {
    return true;
  }
  
  // Only track in production
  return import.meta.env.PROD === true;
}

// ----------------------------------------------------------------------
// Privacy Checks
// ----------------------------------------------------------------------

function hasGlobalPrivacyControl(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    (navigator as unknown as { globalPrivacyControl?: boolean })
      .globalPrivacyControl === true
  );
}

function hasDoNotTrack(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    (navigator.doNotTrack === '1' ||
      (window as any).doNotTrack === '1' ||
      (navigator as any).msDoNotTrack === '1')
  );
}

function hasAnalyticsConsent(): boolean {
  const consent = readCookieConsent();
  return consent?.analytics === true && !hasGlobalPrivacyControl() && !hasDoNotTrack();
}

// ----------------------------------------------------------------------
// Initialization
// ----------------------------------------------------------------------

/**
 * Initialize PostHog if consent is granted and privacy controls allow it
 */
export function initPostHog(): void {
  // Don't initialize if already initialized
  if (posthogInitialized) {
    return;
  }

  // Only track in production (unless explicitly enabled)
  if (!shouldTrackAnalytics()) {
    devLog('[Analytics] Skipping initialization in development mode');
    return;
  }

  // Check if PostHog key is configured
  if (!CONFIG.posthog.key) {
    devWarn('[Analytics] PostHog key not configured');
    return;
  }

  // Check privacy controls and consent
  if (!hasAnalyticsConsent()) {
    return;
  }

  // Initialize PostHog
  try {
    posthog.init(CONFIG.posthog.key, {
      api_host: CONFIG.posthog.host,
      loaded: (ph) => {
        devLog('[Analytics] PostHog initialized');
      },
      // Privacy-focused settings
      capture_pageview: false, // We'll track pageviews manually
      capture_pageleave: true,
      // Don't capture sensitive data
      autocapture: false,
      // Respect user privacy
      respect_dnt: true,
      // Disable session recording by default (can be enabled per user later)
      disable_session_recording: true,
    });

    posthogInitialized = true;
  } catch (error) {
    devError('[Analytics] Failed to initialize PostHog:', error);
  }
}

/**
 * Shutdown PostHog and clear all data
 */
export function shutdownPostHog(): void {
  if (posthogInitialized && typeof posthog !== 'undefined') {
    try {
      posthog.reset();
      posthogInitialized = false;
      devLog('[Analytics] PostHog shutdown');
    } catch (error) {
      devError('[Analytics] Failed to shutdown PostHog:', error);
    }
  }
}

/**
 * Update PostHog state based on current consent
 */
export function updatePostHogConsent(): void {
  if (hasAnalyticsConsent()) {
    if (!posthogInitialized) {
      initPostHog();
    }
  } else {
    shutdownPostHog();
  }
}

// ----------------------------------------------------------------------
// Event Tracking
// ----------------------------------------------------------------------

/**
 * Track a custom event
 */
export function trackEvent(eventName: string, properties?: Record<string, any>): void {
  if (!shouldTrackAnalytics() || !posthogInitialized || !hasAnalyticsConsent()) {
    return;
  }

  try {
    posthog.capture(eventName, properties);
  } catch (error) {
    devError('[Analytics] Failed to track event:', error);
  }
}

/**
 * Track a page view
 */
export function trackPageView(path: string): void {
  if (!shouldTrackAnalytics() || !posthogInitialized || !hasAnalyticsConsent()) {
    return;
  }

  try {
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      path,
    });
  } catch (error) {
    devError('[Analytics] Failed to track page view:', error);
  }
}

/**
 * Identify a user
 */
export function identifyUser(userId: string, properties?: Record<string, any>): void {
  if (!shouldTrackAnalytics() || !posthogInitialized || !hasAnalyticsConsent()) {
    return;
  }

  try {
    posthog.identify(userId, properties);
  } catch (error) {
    devError('[Analytics] Failed to identify user:', error);
  }
}

/**
 * Reset user identification (on logout)
 */
export function resetUser(): void {
  if (!posthogInitialized) {
    return;
  }

  try {
    posthog.reset();
    posthogInitialized = false;
  } catch (error) {
    devError('[Analytics] Failed to reset user:', error);
  }
}

/**
 * Set user properties
 */
export function setUserProperties(properties: Record<string, any>): void {
  if (!shouldTrackAnalytics() || !posthogInitialized || !hasAnalyticsConsent()) {
    return;
  }

  try {
    posthog.setPersonProperties(properties);
  } catch (error) {
    devError('[Analytics] Failed to set user properties:', error);
  }
}

// ----------------------------------------------------------------------
// Listen for consent changes
// ----------------------------------------------------------------------

if (typeof window !== 'undefined') {
  // Listen for cookie consent changes
  window.addEventListener('cookie-consent-changed', () => {
    updatePostHogConsent();
  });

  // Initialize on load if consent already exists
  if (hasAnalyticsConsent()) {
    // Delay initialization slightly to ensure DOM is ready
    setTimeout(() => {
      initPostHog();
    }, 100);
  }
}
